import React, { useMemo, useState, useCallback } from 'react';
import Plot from 'react-plotly.js';
import { useData } from '../../context/DataContext';

const MODEL_COLORS = { A20: '#2471a3', P0: '#27ae60', P6: '#e67e22' };
const TYPE_COLORS = {
  Missense: '#27ae60', Nonsense: '#c0392b', Frameshift: '#2c3e50',
  Splice: '#e67e22', Fusion: '#9b59b6', 'Inframe indel': '#16a085', Other: '#95a5a6',
};
const MODEL_SYMBOLS = { A20: 'circle', P0: 'square', P6: 'diamond' };
const PAGE_SIZE = 50;

// Quick-filter gene sets (murine symbols)
const GENE_SETS = {
  'DLBCL-IQ ABC': [
    'Cd274','Card11','Klhl14','Myd88','Pim1','Ccnd3','Prdm1','H2-K1',
    'Cdkn2a','Trp53','Nlrp8','Atm','Tmem30a','Kmt2c','Pou2af1','Setd1b',
    'Arid1b','Ep300','Braf',
  ],
  'DLBCL-IQ GCB': [
    'Tnfaip3','Socs1','Gna13','Foxo1','Stat3','Il6','Rb1',
  ],
  'Retention/Egress': [
    'Gna13','S1pr2','P2ry8','Rhoa','Arhgef1','Rock1',
    'S1pr1','Cxcr4','Gnai2','Rac2','Foxo1','Klf2',
  ],
  'Common DLBCL': [
    'Kmt2d','Crebbp','Ep300','Bcl2','Bcl6','Myc','Ezh2','Trp53',
    'Prdm1','Card11','Myd88','Pim1','Tnfaip3','Socs1','Gna13',
    'Foxo1','Cdkn2a','Notch1','Notch2','Cd79a','Cd79b','Pax5',
    'Arid1a','Arid1b','Smarca4','Braf','Stat6','Sgk1','Tet2',
    'Dnmt3a','Ikzf3','Nfkbia','Nfkbie','Rel','Btn3a3',
  ],
  'Immune Evasion': [
    'B2m','H2-K1','H2-D1','Tap1','Tap2',
    'Ciita','H2-Aa','H2-Ab1','H2-Eb1','Cd74',
    'Cd274','Cd80','Cd86','Fas',
    'Pvrig','Slamf6','Lag3','Havcr2',
  ],
};

export default function MutationView({ onSelectGene }) {
  const { data } = useData();
  const mutations = data?.mutations;

  const [selectedGene, setSelectedGene] = useState(null);
  const [filterModel, setFilterModel] = useState('All');
  const [filterImpact, setFilterImpact] = useState('All');
  const [filterType, setFilterType] = useState('All');
  const [filterGene, setFilterGene] = useState('');
  const [activeGeneSet, setActiveGeneSet] = useState(null);
  const [sortCol, setSortCol] = useState('gene');
  const [sortAsc, setSortAsc] = useState(true);
  const [page, setPage] = useState(0);

  const allMutations = useMemo(() => mutations?.mutations || [], [mutations]);
  const geneMeta = useMemo(() => mutations?.geneMeta || {}, [mutations]);
  const summary = mutations?.summary || {};
  const sources = mutations?.sources || {};

  // Pre-compute gene set match counts
  const geneSetCounts = useMemo(() => {
    const counts = {};
    for (const [name, genes] of Object.entries(GENE_SETS)) {
      const s = new Set(genes.map(g => g.toLowerCase()));
      counts[name] = allMutations.filter(m => s.has(m.gene.toLowerCase())).length;
    }
    return counts;
  }, [allMutations]);

  // All types present in the data
  const allTypes = useMemo(() => {
    const types = new Set(allMutations.map(m => m.type));
    return ['All', ...['Missense', 'Nonsense', 'Frameshift', 'Splice', 'Fusion', 'Inframe indel', 'Other']
      .filter(t => types.has(t))];
  }, [allMutations]);

  // Gene set as a Set for fast lookup
  const geneSetFilter = useMemo(() => {
    if (!activeGeneSet || !GENE_SETS[activeGeneSet]) return null;
    return new Set(GENE_SETS[activeGeneSet].map(g => g.toLowerCase()));
  }, [activeGeneSet]);

  // Filtered + sorted table data
  const tableData = useMemo(() => {
    let filtered = allMutations;
    if (filterModel !== 'All') filtered = filtered.filter(m => m.model === filterModel);
    if (filterImpact !== 'All') filtered = filtered.filter(m => m.impact === filterImpact);
    if (filterType !== 'All') filtered = filtered.filter(m => m.type === filterType);
    if (geneSetFilter) {
      filtered = filtered.filter(m => geneSetFilter.has(m.gene.toLowerCase()));
    }
    if (filterGene) {
      const q = filterGene.toLowerCase();
      filtered = filtered.filter(m => m.gene.toLowerCase().includes(q));
    }

    const sorted = [...filtered].sort((a, b) => {
      let va = a[sortCol], vb = b[sortCol];
      if (va == null) va = '';
      if (vb == null) vb = '';
      if (typeof va === 'number' && typeof vb === 'number') {
        return sortAsc ? va - vb : vb - va;
      }
      const cmp = String(va).localeCompare(String(vb));
      return sortAsc ? cmp : -cmp;
    });
    return sorted;
  }, [allMutations, filterModel, filterImpact, filterType, filterGene, geneSetFilter, sortCol, sortAsc]);

  // Reset page when filters change
  const filteredCount = tableData.length;
  const totalPages = Math.ceil(filteredCount / PAGE_SIZE);
  const safePage = Math.min(page, totalPages - 1);
  const pageData = tableData.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);

  const handleSort = useCallback((col) => {
    setSortCol(prev => {
      if (prev === col) { setSortAsc(a => !a); return col; }
      setSortAsc(true);
      return col;
    });
    setPage(0);
  }, []);

  const handleGeneClick = useCallback((gene) => {
    setSelectedGene(prev => prev === gene ? null : gene);
  }, []);

  const handleFilterChange = useCallback((setter) => (val) => {
    setter(val);
    setPage(0);
  }, []);

  // Lollipop plot data for selected gene
  const lollipopData = useMemo(() => {
    if (!selectedGene) return null;
    const geneMuts = allMutations.filter(m => m.gene === selectedGene && m.protein_pos != null);
    if (geneMuts.length === 0) return null;

    const proteinLength = geneMeta[selectedGene]?.length || 500;

    const traces = [];
    const models = [...new Set(geneMuts.map(m => m.model))];

    models.forEach(model => {
      const muts = geneMuts.filter(m => m.model === model);
      traces.push({
        x: muts.map(m => m.protein_pos),
        y: muts.map(m => m.vaf != null ? m.vaf : 0.5),
        mode: 'markers',
        type: 'scatter',
        name: model,
        marker: {
          symbol: MODEL_SYMBOLS[model],
          size: 12,
          color: muts.map(m => TYPE_COLORS[m.type] || TYPE_COLORS.Other),
          line: { color: '#fff', width: 1.5 },
        },
        text: muts.map(m => `${m.gene} ${m.hgvs_p || ''}<br>${m.type} (${model})<br>VAF: ${m.vaf != null ? m.vaf.toFixed(3) : 'N/A'}`),
        hoverinfo: 'text',
      });
    });

    const shapes = [
      {
        type: 'rect', x0: 0, x1: proteinLength, y0: -0.02, y1: 0.02,
        fillcolor: '#d5d8dc', line: { width: 0 }, layer: 'below',
      },
    ];
    geneMuts.forEach(m => {
      shapes.push({
        type: 'line',
        x0: m.protein_pos, x1: m.protein_pos,
        y0: 0, y1: m.vaf != null ? m.vaf : 0.5,
        line: { color: '#bbb', width: 1.5 },
        layer: 'below',
      });
    });

    return { traces, shapes, proteinLength };
  }, [selectedGene, allMutations, geneMeta]);

  if (!mutations) {
    return (
      <div className="mutation-view">
        <div className="gene-detail-empty">
          <h3>No mutation data available</h3>
          <p>Run <code>python scripts/export_mutations.py</code> to generate mutation data.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mutation-view">
      {/* Summary cards */}
      <div className="mutation-summary">
        {['A20', 'P0', 'P6'].map(model => {
          const s = summary[model] || {};
          const byType = s.byType || {};
          return (
            <div key={model} className="mutation-summary-card"
                 style={{ borderTopColor: MODEL_COLORS[model] }}>
              <div className="mutation-card-model">{model}</div>
              <div className="mutation-card-count">{s.total?.toLocaleString() || 0}</div>
              <div className="mutation-card-label">coding mutations</div>
              {Object.keys(byType).length > 0 && (
                <div className="mutation-card-breakdown">
                  {Object.entries(byType).sort((a,b) => b[1] - a[1]).map(([type, count]) => (
                    <span key={type} className="breakdown-item">
                      <span className="breakdown-dot" style={{ background: TYPE_COLORS[type] || '#999' }} />
                      {count}
                    </span>
                  ))}
                </div>
              )}
              {sources[model] && (
                <div className="mutation-card-source">
                  {sources[model].source}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Filter controls */}
      <div className="mutation-filters">
        <div className="mutation-filter-group">
          <span className="mutation-filter-label">Model</span>
          {['All', 'A20', 'P0', 'P6'].map(m => (
            <button key={m}
              className={`mutation-filter-btn ${filterModel === m ? 'active' : ''}`}
              onClick={() => handleFilterChange(setFilterModel)(m)}
            >{m}</button>
          ))}
        </div>
        <div className="mutation-filter-group">
          <span className="mutation-filter-label">Impact</span>
          {['All', 'HIGH', 'MODERATE'].map(i => (
            <button key={i}
              className={`mutation-filter-btn ${filterImpact === i ? 'active' : ''}`}
              onClick={() => handleFilterChange(setFilterImpact)(i)}
            >{i}</button>
          ))}
        </div>
        <div className="mutation-filter-group">
          <span className="mutation-filter-label">Type</span>
          {allTypes.map(t => (
            <button key={t}
              className={`mutation-filter-btn ${filterType === t ? 'active' : ''}`}
              onClick={() => handleFilterChange(setFilterType)(t)}
            >{t}</button>
          ))}
        </div>
        <div className="mutation-filter-group">
          <span className="mutation-filter-label">Gene</span>
          <input
            className="mutation-gene-input"
            type="text"
            placeholder="Filter by gene..."
            value={filterGene}
            onChange={e => { setFilterGene(e.target.value); setPage(0); }}
          />
        </div>
      </div>

      {/* Gene set quick filters */}
      <div className="mutation-gene-sets">
        <span className="mutation-filter-label">Gene Set</span>
        <button
          className={`mutation-geneset-btn ${!activeGeneSet ? 'active' : ''}`}
          onClick={() => { setActiveGeneSet(null); setPage(0); }}
        >All genes</button>
        {Object.keys(GENE_SETS).map(name => (
          <button key={name}
            className={`mutation-geneset-btn ${activeGeneSet === name ? 'active' : ''}`}
            onClick={() => { setActiveGeneSet(prev => prev === name ? null : name); setPage(0); }}
          >
            {name} <span className="geneset-count">({geneSetCounts[name] || 0})</span>
          </button>
        ))}
      </div>

      {/* Result count */}
      <div className="mutation-result-count">
        {filteredCount.toLocaleString()} mutation{filteredCount !== 1 ? 's' : ''} shown
        {activeGeneSet && ` in ${activeGeneSet}`}
        {filterGene && ` matching "${filterGene}"`}
      </div>

      {/* Gene lollipop selector — only when gene filter narrows to specific genes */}
      {selectedGene && lollipopData && (
        <div className="mutation-lollipop">
          <Plot
            data={lollipopData.traces}
            layout={{
              width: 800, height: 350,
              margin: { t: 40, b: 50, l: 55, r: 20 },
              title: { text: `${selectedGene} — Protein Mutations`, font: { size: 14, family: 'Inter' } },
              xaxis: {
                title: 'Amino acid position', range: [0, lollipopData.proteinLength],
                zeroline: false, gridcolor: '#f0f0f0',
              },
              yaxis: {
                title: 'VAF', range: [-0.05, 1.1],
                zeroline: false, gridcolor: '#f0f0f0',
              },
              shapes: lollipopData.shapes,
              showlegend: true,
              legend: { orientation: 'h', y: -0.2, x: 0.5, xanchor: 'center', font: { size: 11 } },
              plot_bgcolor: '#fff',
              paper_bgcolor: '#fff',
              font: { family: 'Inter, sans-serif' },
            }}
            config={{ displayModeBar: false, responsive: true }}
          />
          <div className="mutation-type-legend">
            {Object.entries(TYPE_COLORS).filter(([t]) => {
              const geneMuts = allMutations.filter(m => m.gene === selectedGene);
              return geneMuts.some(m => m.type === t);
            }).map(([type, color]) => (
              <span key={type} className="mutation-type-item">
                <span className="mutation-type-dot" style={{ background: color }} />
                {type}
              </span>
            ))}
          </div>
        </div>
      )}

      {selectedGene && !lollipopData && (
        <div className="mutation-no-lollipop">
          <p>No protein positions available for <strong>{selectedGene}</strong> (fusion/structural variants).</p>
        </div>
      )}

      {/* Mutation table */}
      <div className="mutation-table-wrap">
        <table className="mutation-table">
          <thead>
            <tr>
              {[
                { key: 'gene', label: 'Gene' },
                { key: 'model', label: 'Model' },
                { key: 'type', label: 'Type' },
                { key: 'hgvs_p', label: 'HGVSp' },
                { key: 'impact', label: 'Impact' },
                { key: 'chrom', label: 'Chrom' },
                { key: 'pos', label: 'Position' },
                { key: 'vaf', label: 'VAF' },
              ].map(col => (
                <th key={col.key}
                    className={sortCol === col.key ? 'sorted' : ''}
                    onClick={() => handleSort(col.key)}>
                  {col.label}
                  {sortCol === col.key && (
                    <span className="sort-arrow">{sortAsc ? ' ▲' : ' ▼'}</span>
                  )}
                </th>
              ))}
              <th style={{ width: 36 }}></th>
            </tr>
          </thead>
          <tbody>
            {pageData.map((m, i) => (
              <tr key={`${m.chrom}-${m.pos}-${m.alt}-${m.model}-${i}`} className={m.shared ? 'shared-mutation' : ''}>
                <td>
                  <button className="gene-link" onClick={() => handleGeneClick(m.gene)}>
                    {m.gene}
                  </button>
                </td>
                <td><span className={`model-badge model-${m.model.toLowerCase()}`}>{m.model}</span></td>
                <td><span className={`type-badge type-${m.type.toLowerCase().replace(/\s/g, '-')}`}>{m.type}</span></td>
                <td className="mono">{m.hgvs_p || '—'}</td>
                <td><span className={`impact-badge impact-${m.impact.toLowerCase()}`}>{m.impact}</span></td>
                <td className="mono">{m.chrom}</td>
                <td className="mono">{m.pos?.toLocaleString()}</td>
                <td className="mono">{m.vaf != null ? m.vaf.toFixed(3) : '—'}</td>
                <td>
                  {onSelectGene && (
                    <button className="expr-link" title="View expression"
                            onClick={() => onSelectGene(m.gene)}>
                      📊
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {pageData.length === 0 && (
          <p className="mutation-table-empty">No mutations match the current filters.</p>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mutation-pagination">
          <button disabled={safePage === 0} onClick={() => setPage(0)}>«</button>
          <button disabled={safePage === 0} onClick={() => setPage(p => p - 1)}>‹</button>
          <span className="page-info">
            Page {safePage + 1} of {totalPages}
          </span>
          <button disabled={safePage >= totalPages - 1} onClick={() => setPage(p => p + 1)}>›</button>
          <button disabled={safePage >= totalPages - 1} onClick={() => setPage(totalPages - 1)}>»</button>
        </div>
      )}
    </div>
  );
}
